import React, { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { ExternalLink, Search } from 'lucide-react'
import { atlas, enabledStacks } from '../../api'
import { ProfessionKey, PROFESSIONS } from '../../professions'
import Spinner from '../../components/Spinner'
import { detectTimeZone } from '../../countryRules'

type SelectionListItem = {
  selectionId: string
  createdAt: string
  specialty: string
  reason?: string
  booking?: {
    specialistId: string
    slotISO: string
    feeCents: number
    paymentId: string
    createdAt: string
    status?: 'CANCELLED' | 'CONFIRMED'
  } | null
  payment?: {
    bookingFeeCents?: number
    paymentMethodId?: string
    stripeCustomerId?: string
    paymentId?: string
    status?: string
  } | null
  candidates: Array<{
    specialistId: string
    status: 'PENDING' | 'ACCEPTED' | 'DECLINED' | 'LOST' | 'CANCELLED'
    slotISO: string
    feeCents?: number | null
    decisionAt?: string | null
  }>
  profession: ProfessionKey
}

type DecoratedRequest = {
  request: SelectionListItem
  statusText: string
  statusClass: string
  isCurrent: boolean
  isFilled: boolean
  acceptedCount: number
  awaitingResponseCount: number
  pendingCount: number
}

const CONTACT_SLOT = 'REQUEST_BOOKING'
const ENABLED_PROFESSIONS = (enabledStacks.length
  ? (enabledStacks as ProfessionKey[])
  : ['doctor']) as ProfessionKey[]

function isPaid(s: SelectionListItem) {
  const payment = s.payment
  const status = String(payment?.status || '').toLowerCase()
  if (status === 'waived') return true
  if (Number(payment?.bookingFeeCents) === 0) return true
  return status === 'succeeded' || status === 'processing' || status === 'requires_capture'
}

const slotExpired = (iso?: string) => {
  if (iso === CONTACT_SLOT) return false
  if (!iso) return false
  const ts = Date.parse(iso)
  if (!Number.isFinite(ts)) return false
  return ts <= Date.now()
}

const formatCreatedAt = (iso: string, timeZone: string) => {
  return new Date(iso).toLocaleString('en-AU', { timeZone, hour12: false })
}

const decorateRequest = (s: SelectionListItem): DecoratedRequest => {
  const paid = isPaid(s)
  const isFilled = Boolean(s.booking)
  const bookingCancelled = s.booking?.status === 'CANCELLED'
  const bookingPast = isFilled && slotExpired(s.booking?.slotISO)
  const candidates = Array.isArray(s.candidates) ? s.candidates : []
  const hasPending = candidates.some(c => c.status === 'PENDING')
  const hasFuturePending = candidates.some(c => c.status === 'PENDING' && !slotExpired(c.slotISO))
  const requestExpired = paid && !isFilled && hasPending && !hasFuturePending

  const acceptedIds = new Set<string>()
  for (const candidate of candidates) {
    if (candidate.status === 'ACCEPTED' && candidate.specialistId) acceptedIds.add(candidate.specialistId)
  }
  if (s.booking?.specialistId) acceptedIds.add(s.booking.specialistId)
  const acceptedCount = acceptedIds.size
  const awaitingResponseCount = candidates.filter(c => c.status === 'PENDING' && !slotExpired(c.slotISO)).length
  const pendingCount = candidates.filter(c => c.status === 'PENDING' && slotExpired(c.slotISO)).length
  const closedWithoutBooking = paid && !isFilled && !hasPending && acceptedCount === 0

  let statusText = 'Open'
  let statusClass = 'status-pending'
  if (bookingCancelled) {
    statusText = 'Cancelled'
    statusClass = 'status-cancelled'
  } else if (isFilled && bookingPast) {
    statusText = 'Completed'
    statusClass = 'status-accepted'
  } else if (isFilled || acceptedCount > 0) {
    statusText = 'Accepted'
    statusClass = 'status-accepted'
  } else if (!paid) {
    statusText = 'Pending submission'
    statusClass = 'status-pending'
  } else if (requestExpired) {
    statusText = 'Expired'
    statusClass = 'status-lost'
  } else if (closedWithoutBooking) {
    statusText = 'Closed'
    statusClass = 'status-lost'
  }

  const isCurrent = !(bookingCancelled || requestExpired || closedWithoutBooking || bookingPast)
  return {
    request: s,
    statusText,
    statusClass,
    isCurrent,
    isFilled,
    acceptedCount,
    awaitingResponseCount,
    pendingCount,
  }
}

export default function RequestsPage() {
  const [items, setItems] = useState<SelectionListItem[]>([])
  const [loading, setLoading] = useState(true)
  const [err, setErr] = useState<string | null>(null)
  const [deletingId, setDeletingId] = useState<string | null>(null)
  const displayTimeZone = useMemo(() => detectTimeZone(), [])
  const nav = useNavigate()

  const load = async () => {
    setLoading(true)
    setErr(null)
    try {
      const all: SelectionListItem[] = []
      for (const key of ENABLED_PROFESSIONS) {
        try {
          const res = await atlas.selectionsMine(key)
          const itemsForStack = (res?.items ?? []).map((item: any) => ({
            ...item,
            profession: key,
          }))
          all.push(...itemsForStack)
        } catch (e: any) {
          const msg = String(e?.message || '')
          if (msg.toLowerCase().includes('forbidden')) continue
          console.error('selectionsMine failed for', key, e)
        }
      }
      const showable = all
        .filter(s => Boolean(s.booking) || isPaid(s))
        .sort((a, b) => Date.parse(b.createdAt) - Date.parse(a.createdAt))
      setItems(showable)
    } catch (e: any) {
      setErr(e?.message || 'Failed to load requests')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    load()
  }, [])

  const rows = useMemo(() => items.map(decorateRequest), [items])
  const currentRows = rows.filter(row => row.isCurrent)
  const pastRows = rows.filter(row => !row.isCurrent)
  const summary = useMemo(
    () =>
      rows.reduce(
        (acc, row) => {
          acc.accepted += row.acceptedCount
          acc.awaiting += row.awaitingResponseCount
          acc.pending += row.pendingCount
          return acc
        },
        { accepted: 0, awaiting: 0, pending: 0 }
      ),
    [rows]
  )

  const openRequest = (row: DecoratedRequest) => {
    nav(`/requests/${row.request.selectionId}?stack=${row.request.profession}`)
  }

  const renderRequestRow = (row: DecoratedRequest) => {
    const { request } = row
    const allowCancel = !row.isFilled && row.isCurrent
    return (
      <li key={request.selectionId} className="item client-dashboard-item request-entry-item request-entry-item-simple">
        <div className="request-entry-main">
          <div className="request-entry-header">
            <div className="request-entry-name">{request.specialty || 'Request'}</div>
            <span className={`status-pill ${row.statusClass}`}>{row.statusText}</span>
          </div>
          <div className="request-entry-subline">
            <span>{PROFESSIONS[request.profession]?.label || 'Request'}</span>
            <span>Submitted: {formatCreatedAt(request.createdAt, displayTimeZone)}</span>
          </div>
          <div className="request-entry-metrics">
            <span className="pill request-metric-pill">
              Accepted: <strong>{row.acceptedCount}</strong>
            </span>
            <span className="pill request-metric-pill">
              Awaiting: <strong>{row.awaitingResponseCount}</strong>
            </span>
            <span className="pill request-metric-pill">
              Pending: <strong>{row.pendingCount}</strong>
            </span>
          </div>
        </div>
        <div className="list-cta client-dashboard-actions requests-row-actions">
          <button type="button" className="btn btn-outline" onClick={() => openRequest(row)}>
            <ExternalLink size={16} strokeWidth={2.2} />
            View details
          </button>
          {allowCancel && (
            <button
              className="btn btn-outline"
              disabled={deletingId === request.selectionId}
              onClick={async () => {
                if (!confirm('Cancel this request?')) return
                setDeletingId(request.selectionId)
                try {
                  await atlas.cancelSelection(request.profession, request.selectionId)
                  await load()
                } catch (e: any) {
                  alert(e?.message || 'Failed to cancel request')
                } finally {
                  setDeletingId(null)
                }
              }}
            >
              {deletingId === request.selectionId ? 'Cancelling…' : 'Cancel'}
            </button>
          )}
        </div>
      </li>
    )
  }

  return (
    <div className="container page-wide requests-page">
      <div className="page-head">
        <div>
          <p className="hero-kicker">Clients</p>
          <h1>Requests</h1>
          <p className="hint">Review your submitted request details and response counts.</p>
        </div>
        <Link className="btn btn-primary" to="/clients">
          <Search size={16} strokeWidth={2.2} />
          New request
        </Link>
      </div>

      {err && <div className="banner error">{err}</div>}
      {loading && <Spinner label="Loading requests…" />}

      {!loading && !err && rows.length === 0 && (
        <div className="card section-card" style={{ textAlign: 'center', padding: '1.25rem' }}>
          <p style={{ marginBottom: '0.75rem' }}>No requests yet.</p>
          <Link className="btn btn-primary" to="/clients">
            <Search size={16} strokeWidth={2.2} />
            New request
          </Link>
        </div>
      )}

      {!loading && !err && rows.length > 0 && (
        <div className="client-dashboard-grid requests-dashboard-grid">
          <section className="card section-card client-dashboard-summary requests-dashboard-summary">
            <div className="client-dashboard-summary-copy">
              <h3>Response summary</h3>
              <p className="hint">Totals across your current and past requests.</p>
            </div>
            <div className="client-dashboard-stats">
              <div className="client-dashboard-stat">
                <strong>{summary.accepted}</strong>
                <span>Accepted</span>
              </div>
              <div className="client-dashboard-stat">
                <strong>{summary.awaiting}</strong>
                <span>Awaiting response</span>
              </div>
              <div className="client-dashboard-stat">
                <strong>{summary.pending}</strong>
                <span>Pending</span>
              </div>
            </div>
          </section>

          <section className="card section-card client-dashboard-section requests-list-card requests-current-card">
            <header className="client-dashboard-section-head">
              <div>
                <h3>Current requests</h3>
                <p className="hint">Active requests.</p>
              </div>
              <span className="status-pill status-pending">{currentRows.length}</span>
            </header>
            {!currentRows.length && (
              <p className="hint">No current requests.</p>
            )}
            {!!currentRows.length && (
              <ul className="list client-dashboard-list requests-dashboard-list">
                {currentRows.map(renderRequestRow)}
              </ul>
            )}
          </section>

          <section className="card section-card client-dashboard-section requests-list-card requests-past-card">
            <header className="client-dashboard-section-head">
              <div>
                <h3>Past requests</h3>
                <p className="hint">Completed, expired, cancelled, and closed.</p>
              </div>
              <span className="status-pill status-lost">{pastRows.length}</span>
            </header>
            {!pastRows.length && (
              <p className="hint">No past requests yet.</p>
            )}
            {!!pastRows.length && (
              <ul className="list client-dashboard-list requests-dashboard-list">
                {pastRows.map(renderRequestRow)}
              </ul>
            )}
          </section>
        </div>
      )}
    </div>
  )
}
